import { StatusBag } from '@app/enums';
import { Reservation } from '@app/models/micro/reservation.model';
import moment from 'moment';

export class AcillariesMapper {
  static getAncillariesRequest(reservation: Reservation) {
    const { pnr, flightSummary, passengers } = reservation;

    // CI-18515: Construir legCode del PRIMER SEGMENTO solamente
    // Para vuelos con conexión, el backend solo acepta el primer tramo
    const firstSegment = flightSummary?.segments?.[0];
    let legCodeFirstSegment = '';
    if (firstSegment) {
      const departureDate = firstSegment.departureDateTime?.split('T')[0] || '';
      legCodeFirstSegment = `${firstSegment.departureAirport}_${firstSegment.arrivalAirport}_${departureDate}`;
    }

    // Fallback si no se puede construir
    if (!legCodeFirstSegment) {
      legCodeFirstSegment = flightSummary?.legCode || '';
    }

    return {
      // reservationCode: pnr,
      transactionDate: moment(),
      requestFrom: 'FE',
      travelerInfo: passengers
        .filter((passenger) => {
          const hasBagStackToPay = passenger.bagStack?.some(
            (bag) => bag.selected && (bag.status === StatusBag.TO_PAY || bag.isExtraWeight)
          );
          const hasBagStackCarryonToPay = passenger.bagStackCarryon?.some(
            (bag) => bag.selected && bag.status === StatusBag.TO_PAY
          );
          const hasBagOfferCarryOnStackToPay = passenger.bagOfferCarryOnStack?.some(
            (bag) => bag.selected && bag.status === StatusBag.TO_PAY
          );
          return hasBagStackToPay || hasBagStackCarryonToPay || hasBagOfferCarryOnStackToPay;
        })
        .map((passenger) => {
          // Combinar todos los ancillaries: SOLO los que están TO_PAY
          const allBags = [
            ...(passenger.bagStack?.filter(
              (bag) => bag.selected && !bag.isPreLoad && bag.status === StatusBag.TO_PAY
            ) || []),
            ...(passenger.bagStackCarryon?.filter(
              (bag) => bag.selected && bag.status === StatusBag.TO_PAY
            ) || []),
            ...(passenger.bagOfferCarryOnStack?.filter(
              (bag) => bag.selected && bag.status === StatusBag.TO_PAY
            ) || []),
            ...(passenger.bagStack?.filter(
              (bag) => bag.selected && !bag.isPreLoad && bag.isExtraWeight
            ) || []),
          ];

          return {
            nameNumber: passenger.nameRefNumber,
            firstName: passenger.firstName,
            lastName: passenger.lastName,
            ancillaries: allBags.map((bag, index) => {
              const { isExtraWeight } = bag;
              const rficSubcode = isExtraWeight ? bag.extraWeightType : bag.rficSubcode;
              let extraWeightBag;
              let currencyExtra = {};
              if (isExtraWeight) {
                extraWeightBag = reservation?.overWeight.find(
                  (extra) => extra.rficSubcode === bag.extraWeightType
                );
                const extraWeightCurrency = extraWeightBag?.currencyPerLeg?.[0]?.currency || {};
                currencyExtra = {
                  ...extraWeightCurrency,
                  taxDetails: Array.isArray(extraWeightCurrency?.taxDetails)
                    ? extraWeightCurrency.taxDetails.map((tax) => ({
                        ...tax,
                        exempt: false,
                      }))
                    : extraWeightCurrency?.taxDetails || [],
                };
              }

              return {
                ancillary: isExtraWeight
                  ? { ...extraWeightBag, currency: currencyExtra }
                  : {
                      id: bag?.id || '',
                      rficSubcode,
                      commercialName: bag?.commercialName,
                      groupCode: bag?.groupCode,
                      emdType: bag?.emdType,
                      rficCode: bag?.rficCode,
                      ssrCode: bag?.ssrCode,
                      owningCarrierCode: bag?.owningCarrierCode,
                      currency: {
                        currencyCode: bag?.currency?.currencyCode,
                        base: bag?.currency?.base,
                        taxDetails:
                          bag?.currency?.taxDetails?.length > 0
                            ? [
                                {
                                  price: bag?.currency?.taxDetails[0].price ?? 0,
                                  description: bag?.currency?.taxDetails[0].description,
                                  exempt: false,
                                },
                              ]
                            : [
                                {
                                  price: bag?.currencyPerLeg?.[0]?.currency?.totalTax ?? 0,
                                  description: 'MXA',
                                  exempt: false,
                                },
                              ],
                        totalTax: bag?.currency?.totalTax ?? 0,
                        subTotal: bag?.currency?.subTotal ?? 0,
                        total: bag?.currency?.total ?? 0,
                        totalCPPoints: bag?.currency?.totalCPPoints ?? 0,
                      },
                      vendor: bag?.vendor || 'MMGR',
                    },
                legCode: legCodeFirstSegment, // CI-18515: Usar solo primer segmento
                quantity: bag?.quantity || 1,
              };
            }),
          };
        }),
      legs: [
        {
          legCode: legCodeFirstSegment, // CI-18515: Usar solo primer segmento
          legRegion: flightSummary?.legRegion,
          windowLegStatus: flightSummary?.flightStatus,
          // CI-18515: Enviar SOLO el primer segmento para que coincida con legCode
          segments:
            flightSummary?.segments.length === 1
              ? [
                  {
                    segmentCode: firstSegment?.segmentCode,
                    aircraftType: firstSegment?.aircraftType,
                    origin: firstSegment?.departureAirport,
                    farebasis: firstSegment?.farebasis,
                    destination: firstSegment?.arrivalAirport,
                    marketingCarrier: firstSegment?.marketingCarrier,
                    operatingCarrier: firstSegment?.operatingCarrier,
                    operatingFlightCode: firstSegment?.operatingFlightCode,
                    marketingFlightCode: firstSegment?.marketingFlightCode,
                    departureDate: firstSegment?.departureDateTime,
                    arrivalDate: firstSegment?.arrivalDateTime,
                    fareFamily:
                      firstSegment?.fareFamily === 'BASIC'
                        ? 'MAIN_BASIC'
                        : firstSegment?.fareFamily,
                    bookingClass: firstSegment?.bookingClass,
                    bookingCabin: firstSegment?.cabinClass,
                  },
                ]
              : flightSummary?.segments?.map((segment) => ({
                  segmentCode: segment?.segmentCode,
                  aircraftType: segment?.aircraftType,
                  origin: segment?.departureAirport,
                  farebasis: segment?.farebasis,
                  destination: segment?.arrivalAirport,
                  marketingCarrier: segment?.marketingCarrier,
                  operatingCarrier: segment?.operatingCarrier,
                  operatingFlightCode: segment?.operatingFlightCode,
                  marketingFlightCode: segment?.marketingFlightCode,
                  departureDate: segment?.departureDateTime,
                  arrivalDate: segment?.arrivalDateTime,
                  fareFamily: segment?.fareFamily === 'BASIC' ? 'MAIN_BASIC' : segment?.fareFamily,
                  bookingClass: segment?.bookingClass,
                  bookingCabin: segment?.cabinClass,
                })),
        },
      ],
    };
  }

  static mapResponse(response) {
    const { warnings } = response;

    if (warnings && warnings.length > 0) {
      throw new Error(response.warnings[0]);
    }

    // CI-19277: Validar TODOS los ancillaries de TODOS los pasajeros
    for (const traveler of response.travelerInfo || []) {
      for (const ancillaryItem of traveler.ancillaries || []) {
        const reason = ancillaryItem?.ancillary?.reason;
        if (reason) {
          throw new Error(reason);
        }

        // CI-19277: Detectar maletas que quedaron en ONHOLD como señal de fallo silencioso
        const status = ancillaryItem?.ancillary?.status;
        if (status?.toLowerCase() === 'onhold') {
          throw new Error('ANCILLARY_ONHOLD');
        }
      }
    }

    return response;
  }
}
